
// sendForApproval.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Button, Container, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MainScreen from "../../components/MainScreen";
import "./Paginated1.css";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from "../../features/userSlice";

const SendForApproval = () => {
  const [freightOrders, setFreightOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [transporter, setTransporter] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [attachments, setAttachments] = useState({});
  const [bulkFile, setBulkFile] = useState(null);
  const rowsPerPage = 10;

  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;

  useEffect(() => {
    fetchFreightOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [freightOrders, startDate, endDate, transporter, statusFilter]);

  const fetchFreightOrders = async () => {
    try {
      const response = await axios.post("/api/order/getUnapprovedOrders", {
        entity_code: "SE",
        emp_id: "IT-VRAJ",
      });
      setFreightOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const applyFilters = () => {
    let filtered = [...freightOrders];

    if (startDate && endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.VRDATE);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    if (transporter) {
      filtered = filtered.filter(order =>
        order.TRPT_NAME?.toLowerCase().includes(transporter.toLowerCase())
      );
    }

    if (statusFilter !== "All") {
      filtered = filtered.filter(order => (order.STATUS || "Pending") === statusFilter);
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const handleSelect = (vrno, status) => {
    console.log("Status---:", status);
    if (status & status !== "Pending") {
      if (!window.confirm("This record was already sent. Do you want to resend?")) return;
    }
    setSelectedOrders((prev) =>
      prev.includes(vrno) ? prev.filter((id) => id !== vrno) : [...prev, vrno]
    );
  };

  const handleSelectAll = (e) => {
    const allVrnos = currentRows.map((order) => order.VRNO);
    if (e.target.checked) {
      setSelectedOrders(prev => Array.from(new Set([...prev, ...allVrnos])));
    } else {
      setSelectedOrders(prev => prev.filter(vrno => !allVrnos.includes(vrno)));
    }
  };

  const handleAttachmentChange = (vrno, file) => {
    setAttachments(prev => ({ ...prev, [vrno]: file }));
  };

  const handleBulkAttachment = () => {
    if (!bulkFile) return;
    const updated = {};
    selectedOrders.forEach(vrno => {
      updated[vrno] = bulkFile;
    });
    setAttachments(prev => ({ ...prev, ...updated }));
  };

  const handleSubmit = async () => {
    const invalidOrders = selectedOrders.filter(vrno => !attachments[vrno]);
    if (invalidOrders.length > 0) {
      alert("Please attach a file for all selected records.");
      return;
    }

    if (!window.confirm("Are you sure you want to send WhatsApp messages?")) return;

    const ordersToSend = freightOrders.filter((order) => selectedOrders.includes(order.VRNO));
    console.log("order to send:", ordersToSend);
    const formData = new FormData();
    formData.append("orders", JSON.stringify(ordersToSend));

    selectedOrders.forEach(vrno => {
      if (attachments[vrno]) {
        formData.append("files", attachments[vrno], `${vrno}_${attachments[vrno].name}`);
      }
    });
    console.log("formData:", formData);
    try {
      await axios.post("/api/order/sendForApproval", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("WhatsApp messages sent for selected records.");
    } catch (error) {
      console.error("Error sending WhatsApp messages:", error);
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredOrders.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  if (!token) {
    return (
      <div>
        <p>You must be logged in to print daily attendance.</p>
        <button onClick={() => navigate("/")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div style={{ fontSize: "0.8rem" }}>
      <MainScreen title="Send Raw Material Orders For Approval">
        <Container className="paginated-table">

          <div className="d-flex align-items-center gap-4 mb-3 flex-wrap">
            <div className="d-flex align-items-center">
              <label style={{ fontSize: "0.75rem", marginRight: "5px" }}>Transporter:</label>
              <Form.Control
                type="text"
                value={transporter}
                onChange={(e) => setTransporter(e.target.value)}
                placeholder="Enter"
                size="sm"
                style={{ width: "150px" }}
              />
            </div>

            <div className="d-flex align-items-center">
              <label style={{ fontSize: "0.75rem", marginRight: "5px" }}>From Date:</label>
              <DatePicker
                className="form-control form-control-sm"
                selected={startDate}
                onChange={(date) => setStartDate(date)}
              />
            </div>

            <div className="d-flex align-items-center">
              <label style={{ fontSize: "0.75rem", marginRight: "5px" }}>To Date:</label>
              <DatePicker
                className="form-control form-control-sm"
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                minDate={startDate}
              />
            </div>

            <div className="d-flex align-items-center">
              <label style={{ fontSize: "0.75rem", marginRight: "5px" }}>Status:</label>
              <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: "120px" }}>
                <option>All</option>
                <option>Pending</option>
                <option>Sent</option>
                <option>Delivered</option>
              </Form.Select>
            </div>

            <div className="d-flex align-items-center">
              <Form.Control
                type="file"
                size="sm"
                accept="application/pdf"
                onChange={(e) => setBulkFile(e.target.files[0])}
              />
              <Button variant="secondary" size="sm" onClick={handleBulkAttachment} style={{ marginLeft: "5px" }}>
                Apply to Selected
              </Button>
            </div>
          </div>

          <Table striped bordered hover responsive size="sm" className="table">
            <thead>
              <tr style={{ fontSize: "0.75rem", height: "28px" }}>
                <th>
                  <Form.Check
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={currentRows.every((order) => selectedOrders.includes(order.VRNO))}
                  />
                </th>
                <th>Attachment</th>
                <th>Filename</th>
                {freightOrders[0] &&
                  Object.keys(freightOrders[0]).map((key) => (
                    <th key={key}>{key}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((order, idx) => {
                const rowClass =
                  order.STATUS === "delivered"
                    ? "table-success"
                    : order.STATUS === "sent"
                    ? "table-warning"
                    : "table-light";

                return (
                  <tr key={idx} className={rowClass} style={{ fontSize: "0.7rem", height: "28px" }}>

                    <td>
                      <Form.Check
                        type="checkbox"
                        disabled={order.STATUS === "Delivered"}
                        checked={selectedOrders.includes(order.VRNO)}
                        onChange={() => handleSelect(order.VRNO, order.STATUS)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="file"
                        size="sm"
                        accept="application/pdf"
                        onChange={(e) => handleAttachmentChange(order.VRNO, e.target.files[0])}
                        style={{ height: "28px", padding: "0 5px", fontSize: "0.75rem" }}
                      />
                    </td>
                    <td>{attachments[order.VRNO]?.name || ""}</td>
                    {Object.entries(order).map(([key, val], i) => (
                      <td key={i}>
                        {key.toLowerCase().includes("date") && val
                          ? new Date(val).toLocaleDateString()
                          : val?.toString() ?? ""}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="pagination-container mt-3">
            <div className="pagination-controls">
              <div className="pagination-buttons">
                <Button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                  Previous
                </Button>
                <span style={{ paddingTop: "5px" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>

          <div className="text-end mt-3">
            <Button variant="success" disabled={selectedOrders.length === 0} onClick={handleSubmit}>
              Send For Approval
            </Button>
          </div>
        </Container>
      </MainScreen>
    </div>
  );
};

export default SendForApproval;
